import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import axios from 'axios';
import { useSearch } from '../../components/Layout';
import { Badge, Button, Card, EmptyState, Field, HeroPill, List, ListItem, MobileHero, MobilePage, SectionHeader, Select, Skeleton, StatCard } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
const initials = (client) => `${client?.firstName?.[0] || ''}${client?.lastName?.[0] || ''}`.toUpperCase() || 'CL';
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(displayValue).filter(part => part && part !== '—');
    return parts.length ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    const preferred = value.label ?? value.name ?? value.title ?? value.value ?? value.text ?? value.answer ?? value.score ?? value.severity;
    if (preferred !== undefined && preferred !== value) return displayValue(preferred);
    const parts = Object.entries(value)
      .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== '')
      .map(([key, entryValue]) => `${key}: ${displayValue(entryValue)}`);
    return parts.length ? parts.join(', ') : '—';
  }
  return String(value);
};
const clientName = (client) => displayValue(`${client?.firstName || ''} ${client?.lastName || ''}`.trim() || client?.name);
const getClientGenderDisplay = (client) => {
  if (client?.gender === 'other' && client?.genderCustom) return client.genderCustom;
  return client?.gender || client?.sex || '—';
};
const getTestName = (result) => result?.TestCatalog?.shortName || result?.TestCatalog?.name || result?.testName || result?.name || 'Assessment';
const getResultDate = (result) => result?.completedAt || result?.updatedAt || result?.createdAt;
const isTotalKey = (key = '') => String(key).toLowerCase() === 'total';
const pickScoreValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number' || typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.final ?? value.total ?? value.raw ?? value.score ?? value.value ?? '—';
  }
  return value;
};
const getScore = (result) => pickScoreValue(
  result?.totalScore
  ?? result?.score
  ?? result?.scores?.total
  ?? result?.interpretation?.total
  ?? result?.interpretation?.Total
  ?? '—'
);
const scoreNumber = (result) => {
  const score = Number(getScore(result));
  return Number.isFinite(score) ? score : null;
};
const getInterpretationEntries = (result) => Object.entries(result?.interpretation || {})
  .filter(([key, value]) => key && !key.startsWith('_') && !isTotalKey(key) && value && typeof value === 'object' && ('score' in value || 'severity' in value))
  .slice(0, 6);
const getSubscaleKeys = (results) => Array.from(new Set(results.flatMap(result => getInterpretationEntries(result).map(([key]) => key))));
const calculateTrend = (current, previous) => {
  const currentScore = scoreNumber(current);
  const previousScore = scoreNumber(previous);
  if (currentScore === null || previousScore === null) return '';
  if (currentScore < previousScore) return 'Better';
  if (currentScore > previousScore) return 'Worse';
  return 'Same';
};

const TrendChart = ({ results }) => {
  const points = [...results]
    .sort((a, b) => new Date(getResultDate(a) || 0) - new Date(getResultDate(b) || 0))
    .map(result => ({ result, score: scoreNumber(result) }))
    .filter(point => point.score !== null);

  if (points.length < 2) {
    return <EmptyState><strong>Not enough attempts</strong><p>At least 2 numeric scores are needed to show a trend graph.</p></EmptyState>;
  }

  const width = 320;
  const height = 160;
  const pad = 24;
  const maxScore = Math.max(...points.map(point => point.score), 1);
  const minScore = Math.min(...points.map(point => point.score), 0);
  const range = Math.max(maxScore - minScore, 1);
  const svgPoints = points.map((point, idx) => {
    const x = pad + (idx * (width - pad * 2)) / Math.max(points.length - 1, 1);
    const y = height - pad - ((point.score - minScore) * (height - pad * 2)) / range;
    return { ...point, x, y };
  });
  const line = svgPoints.map(point => `${point.x},${point.y}`).join(' ');
  const area = `${pad},${height - pad} ${line} ${width - pad},${height - pad}`;

  return (
    <TrendChartBox>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Score trend chart">
        {[0, 1, 2].map(idx => <line key={idx} x1={pad} x2={width - pad} y1={pad + idx * ((height - pad * 2) / 2)} y2={pad + idx * ((height - pad * 2) / 2)} />)}
        <polygon points={area} />
        <polyline points={line} />
        {svgPoints.map(point => <g key={point.result.id || `${point.x}-${point.y}`}><circle cx={point.x} cy={point.y} r="5" /><text x={point.x} y={Math.max(12, point.y - 10)}>{point.score}</text></g>)}
      </svg>
      <TrendLabels>{svgPoints.map(point => <span key={point.result.id || getResultDate(point.result)}>{formatDate(getResultDate(point.result))}</span>)}</TrendLabels>
    </TrendChartBox>
  );
};

const MobileClients = () => {
  const { searchQuery } = useSearch();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeMode, setMergeMode] = useState(false);
  const [autoMergeSummary, setAutoMergeSummary] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await axios.get(`${API_URL}/practitioner/clients`);
        setClients(res.data.clients || res.data || []);
        setAutoMergeSummary(res.data.autoMerge || null);
      } catch (error) {
        console.error('Mobile clients load error:', error);
      } finally {
        setLoading(false);
      }
    };
    loadClients();
  }, []);

  const filteredClients = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return clients.filter(client => `${client.firstName || ''} ${client.lastName || ''} ${client.email || ''}`.toLowerCase().includes(q));
  }, [clients, searchQuery]);

  useEffect(() => {
    const hasOpenClientModal = Boolean(selectedClient || graphData);
    document.body.classList.toggle('client-detail-open', hasOpenClientModal);

    if (!hasOpenClientModal) return () => document.body.classList.remove('client-detail-open');

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('client-detail-open');
    };
  }, [selectedClient, graphData]);

  const activeClients = clients.filter(client => Number(client.completedTests || client.testCount || client.totalTests || 0) > 0).length;
  const groupedHistory = useMemo(() => history.reduce((groups, result) => {
    const name = getTestName(result);
    if (!groups[name]) groups[name] = [];
    groups[name].push(result);
    return groups;
  }, {}), [history]);

  const toggleMergeSelection = (clientId) => {
    setSelectedForMerge(prev => prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]);
    setMergeTarget(current => current || clientId);
  };

  const mergeClients = async () => {
    if (!mergeTarget || selectedForMerge.length < 2) {
      alert('Please select a target client and at least one client to merge');
      return;
    }
    try {
      const duplicateClientIds = selectedForMerge.filter(id => String(id) !== String(mergeTarget));
      await axios.post(`${API_URL}/practitioner/clients/merge`, { targetClientId: mergeTarget, duplicateClientIds });
      alert('Clients merged successfully!');
      setMergeMode(false);
      setSelectedForMerge([]);
      setMergeTarget('');
      setLoading(true);
      const res = await axios.get(`${API_URL}/practitioner/clients`);
      setClients(res.data.clients || res.data || []);
    } catch (error) {
      alert('Failed to merge clients: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const openClient = async (client) => {
    setSelectedClient(client);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/practitioner/clients/${client.id}/history`);
      setHistory(res.data.history || res.data.tests || []);
    } catch (error) {
      console.error('Mobile client history error:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <MobilePage>
      <MobileHero>
        <HeroPill>Client Directory</HeroPill>
        <h2>Clients</h2>
        <p>Browse your client list, open individual histories, and keep assessment progress visible.</p>
      </MobileHero>

      {loading ? <Skeleton $height="320px" /> : (
        <>
          <StatsGrid>
            <StatCard $accent="#2563eb"><span>Total clients</span><strong>{clients.length}</strong><small>Directory</small></StatCard>
            <StatCard $accent="#059669"><span>Active</span><strong>{activeClients}</strong><small>With history</small></StatCard>
          </StatsGrid>

          {autoMergeSummary?.mergedClientsCount > 0 && <Notice><strong>Auto-merged duplicates</strong><span>{autoMergeSummary.mergedClientsCount} duplicate client{autoMergeSummary.mergedClientsCount === 1 ? '' : 's'} merged by email.</span></Notice>}

          <Card>
            <SectionHeader><div><span>Directory</span><h3>{filteredClients.length} client{filteredClients.length === 1 ? '' : 's'}</h3></div><Button $secondary type="button" onClick={() => setMergeMode(!mergeMode)}>{mergeMode ? 'Cancel' : 'Merge'}</Button></SectionHeader>
            {mergeMode && <MergePanel><Field>Target client<Select value={mergeTarget} onChange={event => setMergeTarget(event.target.value)}><option value="">Choose target</option>{clients.filter(client => selectedForMerge.includes(client.id)).map(client => <option key={client.id} value={client.id}>{clientName(client)}</option>)}</Select></Field><Button $full type="button" disabled={selectedForMerge.length < 2 || !mergeTarget} onClick={mergeClients}>Merge selected clients</Button></MergePanel>}
            {filteredClients.length === 0 ? <EmptyState><strong>No clients found</strong><p>Try searching by name or email.</p></EmptyState> : (
              <List>
                {filteredClients.map(client => (
                  <ListItem key={client.id} type="button" onClick={() => mergeMode ? toggleMergeSelection(client.id) : openClient(client)}>
                    {mergeMode && <CheckDot $active={selectedForMerge.includes(client.id)}>{selectedForMerge.includes(client.id) ? '✓' : ''}</CheckDot>}
                    <Avatar>{initials(client)}</Avatar>
                    <ClientCopy>
                      <strong>{clientName(client)}</strong>
                      <span>{displayValue(client.email || 'No email')} • {displayValue(client.phone || 'No phone')}</span>
                    </ClientCopy>
                    <Badge>{displayValue(client.completedTests || client.testCount || client.totalTests || 0)} tests</Badge>
                  </ListItem>
                ))}
              </List>
            )}
          </Card>
        </>
      )}

      {selectedClient && createPortal(
        <SheetBackdrop role="dialog" aria-modal="true" onClick={() => setSelectedClient(null)}>
          <DetailSheet onClick={event => event.stopPropagation()}>
            <Handle />
            <SheetTopRow>
              <div>
                <span>Client profile</span>
                <h3>{clientName(selectedClient)}</h3>
              </div>
              <Button $secondary type="button" onClick={() => setSelectedClient(null)}>Close</Button>
            </SheetTopRow>
            <ClientProfileCard>
              <LargeAvatar>{initials(selectedClient)}</LargeAvatar>
              <div>
                <strong>{clientName(selectedClient)}</strong>
                <span>{displayValue(selectedClient.email)} • {displayValue(selectedClient.phone || 'No phone')}</span>
              </div>
            </ClientProfileCard>

            <MobileSection>
              <SectionHeader><div><span>Demographics</span><h3>Client information</h3></div></SectionHeader>
              <DemoGrid>
                <div><span>First Name</span><strong>{displayValue(selectedClient.firstName)}</strong></div>
                <div><span>Last Name</span><strong>{displayValue(selectedClient.lastName)}</strong></div>
                <div><span>Age</span><strong>{displayValue(selectedClient.age || 'N/A')}</strong></div>
                <div><span>Gender</span><strong>{displayValue(getClientGenderDisplay(selectedClient))}</strong></div>
                <div><span>Email</span><strong>{displayValue(selectedClient.email)}</strong></div>
                <div><span>Phone</span><strong>{displayValue(selectedClient.phone || 'No phone')}</strong></div>
                <div><span>Education</span><strong>{displayValue(selectedClient.education)}</strong></div>
                <div><span>Occupation</span><strong>{displayValue(selectedClient.occupation)}</strong></div>
                <div><span>Last Test</span><strong>{formatDate(selectedClient.lastTestDate || selectedClient.lastCompletedAt || selectedClient.updatedAt)}</strong></div>
                <div><span>Total Tests</span><strong>{displayValue(selectedClient.completedTests || selectedClient.testCount || selectedClient.totalTests || 0)}</strong></div>
              </DemoGrid>
            </MobileSection>

            <MobileSection>
              <SectionHeader><div><span>Assessment history</span><h3>{history.length} completed record{history.length === 1 ? '' : 's'}</h3></div></SectionHeader>
              {historyLoading ? <Skeleton $height="180px" /> : history.length === 0 ? <EmptyState><strong>No history</strong><p>This client does not have completed test history yet.</p></EmptyState> : (
                <HistoryGroups>
                  {Object.entries(groupedHistory).map(([testName, groupedResults]) => {
                    const sortedResults = [...groupedResults].sort((a, b) => new Date(getResultDate(b) || 0) - new Date(getResultDate(a) || 0));
                    return (
                      <TestGroupCard key={testName}>
                        <TestGroupHead>
                          <div>
                            <strong>{displayValue(testName)}</strong>
                            <span>{sortedResults.length} attempt{sortedResults.length === 1 ? '' : 's'}</span>
                          </div>
                          {sortedResults.length > 1 && <Button $secondary type="button" onClick={() => setGraphData({ testName, results: sortedResults })}>View Graph</Button>}
                        </TestGroupHead>
                        <ScrollHint>Swipe to compare all columns</ScrollHint>
                        <TableScroller>
                          <CompareTable>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Total</th>
                                {getSubscaleKeys(sortedResults).map(key => <th key={key}>{displayValue(key)}</th>)}
                                <th>vs Prev</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedResults.map((item, idx) => {
                                const previous = sortedResults[idx + 1];
                                const interp = item.interpretation || {};
                                return (
                                  <tr key={item.id || `${testName}-${idx}`}>
                                    <td><DateCell><strong>{formatDate(getResultDate(item))}</strong><span>{displayValue(item.status || 'completed')}</span></DateCell></td>
                                    <td><TotalCell><strong>{displayValue(getScore(item))}</strong><span>Total</span></TotalCell></td>
                                    {getSubscaleKeys(sortedResults).map(key => (
                                      <td key={key}>
                                        {interp[key] ? <SubscaleCell><strong>{displayValue(pickScoreValue(interp[key].score))}</strong><span>{displayValue(interp[key].severity)}</span></SubscaleCell> : <MutedDash>—</MutedDash>}
                                      </td>
                                    ))}
                                    <td>{previous ? <TrendBadge>{calculateTrend(item, previous) || 'Same'}</TrendBadge> : <MutedDash>First</MutedDash>}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </CompareTable>
                        </TableScroller>
                      </TestGroupCard>
                    );
                  })}
                </HistoryGroups>
              )}
            </MobileSection>
          </DetailSheet>
        </SheetBackdrop>,
        document.body
      )}

      {graphData && createPortal(
        <SheetBackdrop role="dialog" aria-modal="true" onClick={() => setGraphData(null)}>
          <DetailSheet onClick={event => event.stopPropagation()}>
            <Handle />
            <SectionHeader><div><span>Score trend</span><h3>{displayValue(graphData.testName)}</h3></div><Button $secondary type="button" onClick={() => setGraphData(null)}>Close</Button></SectionHeader>
            <TrendChart results={graphData.results} />
          </DetailSheet>
        </SheetBackdrop>,
        document.body
      )}
    </MobilePage>
  );
};

const StatsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const Notice = styled.div`
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 20px;
  padding: 13px;
  strong { color: #166534; display: block; font-size: 0.88rem; }
  span { color: #047857; display: block; font-size: 0.76rem; margin-top: 4px; }
`;

const MergePanel = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
`;

const CheckDot = styled.div`
  align-items: center;
  background: ${p => (p.$active ? '#2563eb' : '#e2e8f0')};
  border-radius: 50%;
  color: white;
  display: flex;
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 950;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

const Avatar = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  border-radius: 17px;
  color: white;
  display: flex;
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 950;
  height: 46px;
  justify-content: center;
  width: 46px;
`;

const ClientCopy = styled.div`
  flex: 1;
  min-width: 0;
  strong { color: #0f172a; display: block; font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  span { color: #64748b; display: block; font-size: 0.74rem; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const SheetBackdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: max(10px, env(safe-area-inset-top)) 10px max(10px, env(safe-area-inset-bottom));
  position: fixed;
  z-index: 2000;
`;

const DetailSheet = styled.section`
  background: white;
  border-radius: 18px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
  max-height: calc(100dvh - 20px);
  max-width: 430px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px;
  width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const Handle = styled.div`
  display: none;
`;

const SheetTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
  span { color: #64748b; display: block; font-size: 0.72rem; font-weight: 950; letter-spacing: 0.09em; text-transform: uppercase; }
  h3 { color: #0f172a; font-size: 1.28rem; line-height: 1.1; margin: 4px 0 0; }
`;

const ClientProfileCard = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #eff6ff, #f8fafc);
  border: 1px solid #dbeafe;
  border-radius: 22px;
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  strong { color: #0f172a; display: block; font-size: 0.98rem; }
  span { color: #64748b; display: block; font-size: 0.78rem; line-height: 1.4; margin-top: 4px; word-break: break-word; }
`;

const LargeAvatar = styled(Avatar)`
  border-radius: 22px;
  font-size: 1rem;
  height: 58px;
  width: 58px;
`;

const MobileSection = styled.section`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  margin-bottom: 12px;
  padding: 12px;
`;

const HistoryGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TestGroupCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
`;

const TestGroupHead = styled.div`
  align-items: center;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 12px;
  strong { color: #0f172a; display: block; font-size: 0.92rem; }
  span { color: #64748b; display: block; font-size: 0.74rem; margin-top: 3px; }
`;

const ScrollHint = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 850;
  padding: 9px 12px 0;
`;

const TableScroller = styled.div`
  overflow-x: auto;
  padding: 10px;
  -webkit-overflow-scrolling: touch;
`;

const CompareTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  min-width: 620px;
  width: 100%;
  th { background: linear-gradient(135deg, #1d4ed8, #0369a1); color: white; font-size: 0.68rem; padding: 10px; text-align: left; text-transform: uppercase; }
  th:first-of-type { border-radius: 10px 0 0 0; }
  th:last-of-type { border-radius: 0 10px 0 0; }
  td { background: white; border-bottom: 1px solid #e2e8f0; color: #0f172a; font-size: 0.76rem; padding: 10px; vertical-align: middle; }
`;

const DateCell = styled.div`
  strong { color: #0f172a; display: block; font-size: 0.76rem; }
  span { color: #64748b; display: block; font-size: 0.68rem; margin-top: 2px; text-transform: capitalize; }
`;

const TotalCell = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  min-width: 54px;
  padding: 6px;
  text-align: center;
  strong { color: #1d4ed8; display: block; font-size: 0.9rem; }
  span { color: #2563eb; display: block; font-size: 0.58rem; font-weight: 950; text-transform: uppercase; }
`;

const SubscaleCell = styled.div`
  min-width: 86px;
  strong { color: #0f172a; display: inline-block; font-size: 0.76rem; margin-right: 6px; }
  span { background: #dbeafe; border-radius: 999px; color: #1d4ed8; display: inline-block; font-size: 0.62rem; font-weight: 900; max-width: 110px; overflow: hidden; padding: 3px 7px; text-overflow: ellipsis; vertical-align: middle; white-space: nowrap; }
`;

const TrendBadge = styled.span`
  background: #dcfce7;
  border-radius: 999px;
  color: #166534;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 900;
  padding: 5px 8px;
`;

const MutedDash = styled.span`
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 850;
`;







const DemoGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 12px;
  div { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 10px; }
  span { color: #64748b; display: block; font-size: 0.68rem; font-weight: 900; text-transform: uppercase; }
  strong { color: #0f172a; display: block; font-size: 0.8rem; margin-top: 4px; }
`;



const TrendChartBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 10px;
  svg { display: block; width: 100%; height: auto; }
  line { stroke: #dbeafe; stroke-dasharray: 5 5; }
  polygon { fill: rgba(37, 99, 235, 0.12); }
  polyline { fill: none; stroke: #2563eb; stroke-linecap: round; stroke-linejoin: round; stroke-width: 4; }
  circle { fill: white; stroke: #2563eb; stroke-width: 4; }
  text { fill: #1d4ed8; font-size: 0.8rem; font-weight: 900; text-anchor: middle; }
`;

const TrendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
  span { color: #64748b; font-size: 0.68rem; font-weight: 850; text-align: center; }
`;

export default MobileClients;
